/**
 * PhaseDependentLayout — viewer chrome for `phase_dependent` projects.
 *
 * Fast path (≤4 phases, the common case): each phase gets a permanent slot
 * assigned once. Cycling between phases via Tab / `\` / tab-click moves the
 * spotlight (`maximizedSlotAtom`) to the slot holding the active phase — no
 * SET_SERIES dispatch, no slot state reset, no series re-load. Flicker
 * rotates the spotlight through the per-phase slots at `flickerHzAtom`.
 *
 * Legacy slow path (>4 phases): top-left carries the active phase and the
 * other slots rotate around it via SET_SERIES. Used only when there aren't
 * enough physical slots to give each phase a permanent home.
 *
 * Responsibilities:
 *  - Classify series in the study into phases (`phaseClassifier.ts`).
 *  - Populate `availableDiscriminatorsAtom`, seed `activeDiscriminatorAtom`
 *    (PV when present, else arterial).
 *  - Pick the assignment strategy above and wire active → spotlight (or
 *    active → top-left, for the legacy path).
 *  - Refit cornerstone cameras after spotlight resizes the slots (a 2-raf
 *    delay so CSS grid has applied the new track sizes).
 *  - Drive flicker on the chosen path; release snaps spotlight back to
 *    the active phase's slot.
 */
import React, { useCallback, useEffect, useMemo } from "react";
import { useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { HangingProtocolGrid, HangingProtocolGridProps } from "./HangingProtocolGrid";
import { logger } from "@/utils/logger";
import {
  activeDiscriminatorAtom,
  availableDiscriminatorsAtom,
  flickerActiveAtom,
  flickerHzAtom,
} from "../../atoms/gatingAtoms";
import {
  hangingProtocolActionsAtom,
  layoutModeAtom,
  maximizedSlotAtom,
  slotStateFamily,
} from "../../atoms/hangingProtocolAtoms";
import { getCornerstone } from "../../utils/dicomLoader";
import { SHARED_RENDERING_ENGINE_ID } from "../../utils/renderingEngine";
import {
  DEFAULT_SEGMENTATION_PHASE,
  PHASE_ORDER,
  type Discriminator,
  type Phase,
} from "../../types/gating";
import { classifySeriesPhase } from "../../utils/phaseClassifier";
import { DEFAULT_SLOT, isScoutSeries, type SlotId } from "../../utils/hangingProtocolService";

/**
 * Slot fill order for phase series. The first three slots are always phase
 * viewports; the fourth slot only takes a phase when phases.length >= 4, and
 * is otherwise occupied by the 3D model (≤3) or a summary card (≥5).
 */
const PHASE_SLOTS: SlotId[] = ["top-left", "top-right", "bottom-left", "bottom-right"];

/**
 * Run `fn` after two animation frames so CSS grid has applied any pending
 * track-size changes before `fn` measures the canvas. Returns a cancel
 * function for use in effect cleanup. One raf isn't enough on some renderers.
 */
function scheduleAfterLayout(fn: () => void): () => void {
  let raf2 = 0;
  const raf1 = requestAnimationFrame(() => {
    raf2 = requestAnimationFrame(fn);
  });
  return () => {
    cancelAnimationFrame(raf1);
    cancelAnimationFrame(raf2);
  };
}

export type PhaseDependentLayoutProps = HangingProtocolGridProps;

export const PhaseDependentLayout: React.FC<PhaseDependentLayoutProps> = (props) => {
  const { studyMetadata, studyId } = props;
  const setAvailable = useSetAtom(availableDiscriminatorsAtom);
  const [active, setActive] = useAtom(activeDiscriminatorAtom);
  const dispatch = useSetAtom(hangingProtocolActionsAtom);
  const setLayoutMode = useSetAtom(layoutModeAtom);
  const setMaximizedSlot = useSetAtom(maximizedSlotAtom);
  const flickerActive = useAtomValue(flickerActiveAtom);
  const flickerHz = useAtomValue(flickerHzAtom);
  const store = useStore();

  // hl3d always lays out as 2x2 — the four-phase grid + 3D-or-summary corner
  // is the entire point of phase_dependent. This intentionally overrides a
  // persisted `layoutModeAtom` from a non-hl3d session; the next non-hl3d
  // page restores its own choice. Maximized-slot is only seeded when nothing
  // is set, so revisiting a study keeps whichever slot the user spotlighted
  // last (and the active-phase effect below still moves it on phase change).
  useEffect(() => {
    setLayoutMode("2x2");
    if (store.get(maximizedSlotAtom) === null) {
      setMaximizedSlot(DEFAULT_SLOT);
    }
  }, [setLayoutMode, setMaximizedSlot, store]);

  /** Classify the study's series and populate the available phase atom. */
  const phaseDiscriminators = useMemo<Discriminator[]>(() => {
    const seriesMap = studyMetadata?.series_metadata;
    if (!seriesMap) return [];
    const seen = new Map<Phase, string>();
    // Coverage counter — without this, a broken heuristic looks identical to
    // one that never triggered (CLAUDE.md root rule).
    let candidates = 0;
    let empty = 0;
    for (const meta of Object.values(seriesMap)) {
      if (isScoutSeries(meta)) continue;
      // Non-reconstructable series don't expose the volume APIs
      // `useHangingProtocolSync` reads (no consistent IPP / spacing). The cell
      // would render but silently drop out of scroll sync — skip entirely so
      // the phase tab bar only offers MPR-capable series.
      if (meta.is_reconstructable === false) continue;
      candidates++;
      const phase = classifySeriesPhase(meta.series_description, null);
      if (!phase) {
        empty++;
        continue;
      }
      // First match per phase wins; later duplicates ignored. The cohort
      // guarantees at least one phase per study.
      if (!seen.has(phase)) seen.set(phase, meta.series_uid);
    }
    logger.info(
      "phase_classifier_coverage feature=phase_dependent_layout cohort=%s study=%s candidates=%d empty=%d",
      "phase_dependent",
      studyId,
      candidates,
      empty,
    );
    return PHASE_ORDER.flatMap<Discriminator>((p) =>
      seen.has(p) ? [{ kind: "phase", value: p, seriesInstanceUid: seen.get(p)! }] : [],
    );
  }, [studyMetadata, studyId]);

  useEffect(() => {
    setAvailable(phaseDiscriminators);
  }, [phaseDiscriminators, setAvailable]);

  /** Seed active phase: PV if present, else arterial, else first available. */
  useEffect(() => {
    if (
      active &&
      phaseDiscriminators.some((d) => d.seriesInstanceUid === active.seriesInstanceUid)
    ) {
      return;
    }
    const pick =
      phaseDiscriminators.find((d) => d.value === DEFAULT_SEGMENTATION_PHASE) ??
      phaseDiscriminators.find((d) => d.value === "arterial") ??
      phaseDiscriminators[0] ??
      null;
    setActive(pick);
  }, [phaseDiscriminators, active, setActive]);

  /**
   * For ≤4 phases, each phase gets a permanent slot. Cycling between phases
   * then just moves the spotlight — no SET_SERIES dispatch, no slot state
   * reset, instant. For >4 phases we fall back to the legacy rotation because
   * phases past the third have no permanent home slot.
   */
  const stableAssignments = useMemo<Map<string, SlotId> | null>(() => {
    if (phaseDiscriminators.length === 0 || phaseDiscriminators.length > 4) return null;
    const fillCount =
      phaseDiscriminators.length === 4 ? 4 : Math.min(phaseDiscriminators.length, 3);
    const map = new Map<string, SlotId>();
    for (let i = 0; i < fillCount; i++) {
      map.set(phaseDiscriminators[i].seriesInstanceUid, PHASE_SLOTS[i]);
    }
    return map;
  }, [phaseDiscriminators]);

  /**
   * Fast path (≤4 phases): assign each phase to a permanent slot once. From
   * then on, `]`/`[`/`\`/tab-click just changes which slot is spotlighted —
   * no series re-load, no slot state reset.
   *
   * Skip the dispatch when a slot is already showing its assigned series.
   * `SET_SERIES` resets `sliceIndex`, `windowing`, and `isInverted` (full
   * `createDefaultSlotState`) — re-firing it would wipe the user's
   * mid-session adjustments. The skip is safe because seriesId is the
   * identity here; a real "reassign this slot to a different series" still
   * fires through (seriesId differs).
   */
  useEffect(() => {
    if (!stableAssignments) return;
    stableAssignments.forEach((slotId, seriesId) => {
      if (store.get(slotStateFamily(slotId)).seriesId === seriesId) return;
      dispatch({ type: "SET_SERIES", slotId, seriesId });
    });
  }, [stableAssignments, dispatch, store]);

  /**
   * Refit phase viewports against their current canvas sizes after the
   * spotlight resized the slots. `resize(immediate, keepCamera=true)` rescales
   * each viewport's parallel-scale to match its new canvas while preserving
   * the radiologist's zoom/pan in world coordinates — keepCamera=false (or a
   * per-viewport `resetCamera`) would wipe the inspection state the user just
   * set up to compare a lesion across phases.
   */
  const refitPhaseViewports = useCallback(() => {
    if (!stableAssignments) return;
    // User Timing marks so a Chrome perf recording during hold-`\` surfaces
    // each resize tick under "phase-refit" without leaving heavy logging on.
    // Tracks #392 — confirms whether the 12 Hz resize loop stays under
    // budget on large hl3d volumes. No-op cost in prod. (#392)
    //
    // Guard mark + measure together: a polyfill that supplies only one would
    // leave t0=0 and produce a bogus enormous-ms >16ms log on every tick.
    const perfAvailable =
      typeof performance !== "undefined" && !!performance.mark && !!performance.measure;
    const t0 = perfAvailable ? (performance.mark("phase-refit:start"), performance.now()) : 0;
    try {
      const engine = getCornerstone().getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
      engine?.resize(true, true);
    } catch (err) {
      logger.debug("[PhaseDependentLayout] refit skipped (cornerstone not ready):", err);
    }
    if (perfAvailable) {
      try {
        performance.measure("phase-refit", "phase-refit:start");
      } catch (err) {
        logger.debug("[PhaseDependentLayout] performance.measure failed:", err);
      }
      const ms = performance.now() - t0;
      if (ms > 16) logger.debug(`[PhaseDependentLayout] refit ${ms.toFixed(1)}ms (>16ms budget)`);
    }
  }, [stableAssignments]);

  /** Fast path: spotlight follows the active phase. Pure layout change. */
  useEffect(() => {
    if (!stableAssignments || flickerActive || !active) return;
    const slot = stableAssignments.get(active.seriesInstanceUid);
    if (!slot) return;
    setMaximizedSlot(slot);
    return scheduleAfterLayout(refitPhaseViewports);
  }, [active, stableAssignments, flickerActive, setMaximizedSlot, refitPhaseViewports]);

  /** Fast-path flicker: rotate the spotlight through per-phase slots. */
  useEffect(() => {
    if (!stableAssignments || !flickerActive) return;
    const slots = phaseDiscriminators
      .map((d) => stableAssignments.get(d.seriesInstanceUid))
      .filter((s): s is SlotId => !!s);
    if (slots.length < 2) return;
    const activeSlot = active ? (stableAssignments.get(active.seriesInstanceUid) ?? null) : null;
    let i = activeSlot ? Math.max(0, slots.indexOf(activeSlot)) : 0;

    let cancelRefit: (() => void) | null = null;
    const interval = setInterval(
      () => {
        i = (i + 1) % slots.length;
        setMaximizedSlot(slots[i]);
        cancelRefit?.();
        cancelRefit = scheduleAfterLayout(refitPhaseViewports);
      },
      Math.max(50, Math.round(1000 / flickerHz)),
    );
    return () => {
      clearInterval(interval);
      cancelRefit?.();
      const restore = active ? (stableAssignments.get(active.seriesInstanceUid) ?? null) : null;
      if (restore) setMaximizedSlot(restore);
      cancelRefit = scheduleAfterLayout(refitPhaseViewports);
    };
  }, [
    flickerActive,
    flickerHz,
    phaseDiscriminators,
    active,
    stableAssignments,
    setMaximizedSlot,
    refitPhaseViewports,
  ]);

  /**
   * Legacy slow path (>4 phases): active phase pulled into top-left, others
   * rotated around it via SET_SERIES. Only used when no stable assignment
   * exists because phases past the third have no permanent slot.
   */
  useEffect(() => {
    if (stableAssignments) return;
    if (flickerActive || phaseDiscriminators.length === 0) return;
    const activeUid = active?.seriesInstanceUid ?? null;
    const ordered: Discriminator[] = [];
    if (activeUid) {
      const found = phaseDiscriminators.find((d) => d.seriesInstanceUid === activeUid);
      if (found) ordered.push(found);
    }
    for (const d of phaseDiscriminators) {
      if (d.seriesInstanceUid === activeUid) continue;
      ordered.push(d);
    }
    const fillCount = Math.min(ordered.length, 3);
    for (let i = 0; i < fillCount; i++) {
      dispatch({
        type: "SET_SERIES",
        slotId: PHASE_SLOTS[i],
        seriesId: ordered[i].seriesInstanceUid,
      });
    }
  }, [active, phaseDiscriminators, flickerActive, dispatch, stableAssignments]);

  /** Legacy flicker for the >4-phase slow path. */
  useEffect(() => {
    if (stableAssignments) return;
    if (!flickerActive || phaseDiscriminators.length < 2) return;
    const startPhase = active?.kind === "phase" ? (active.value as Phase) : null;
    let i = startPhase
      ? Math.max(
          0,
          phaseDiscriminators.findIndex((d) => d.value === startPhase),
        )
      : 0;
    const interval = setInterval(
      () => {
        i = (i + 1) % phaseDiscriminators.length;
        const next = phaseDiscriminators[i];
        dispatch({
          type: "SET_SERIES",
          slotId: DEFAULT_SLOT,
          seriesId: next.seriesInstanceUid,
        });
      },
      Math.max(50, Math.round(1000 / flickerHz)),
    );
    return () => clearInterval(interval);
  }, [flickerActive, flickerHz, phaseDiscriminators, active, dispatch, stableAssignments]);

  // The PhaseTabBar lives in the right annotation panel — this layout just
  // owns slot/series wiring and renders the grid full-bleed.
  return <HangingProtocolGrid {...props} />;
};
